/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ACTION,
  ValueChange,
  RequestImmediateBackgroundSave,
  RequestCodetableCategory,
  RequestCodetableCategoryError,
  ReceiveCodetableCategory
} from './action-types';
import { Action, Reducer } from 'redux';
import {
  RequestCodetable,
  RequestCodetableError,
  ReceiveCodetable
} from './action-types';
import {
  BeginBackgroundSave,
  BackgroundSaveComplete,
  BackgroundSaveFailed,
  BeginCaseSave,
  RequestCaseSave,
  CaseSaveComplete,
  CaseSaveFailed
} from './action-types';
import { deepEquals } from 'app/infrastructure/js';
import { toJSON } from 'app/infrastructure/platform';
import { UiState, EditState } from './types';
import { CaseState } from 'app/area/case/types-store';
import { codeItemToStoreValue } from '../../data/codes';
import { StoreData } from 'app/types-shared';
import { getById } from 'app/infrastructure/store/query';
import { getPatchedState } from 'app/infrastructure/store/patch';

interface ActionMap<T> {
  [indexer: string]: Reducer<T, Action>;
}

const INITIAL_EDIT_STATE: EditState = {
  // current values
  values: [],

  // current metadata
  metadata: { domains: {} },

  // validation errors returned from api
  loadValidationMetadata: false,

  // etag from last save
  etag: null,

  // used to mark that api operation was triggered and should not begin another one
  fetching: false,

  // used to mark that case submitting was triggered in ui
  submitting: false,

  // the delta of persisted and current
  hasChanges: false,

  // used to revert pending changes, avaliable until background save is done
  // processing
  persistedValues: [],

  // No background saves has run yet
  pristine: true,

  // used to indciate if it should stay in edit mode after submitting
  continueEditing: false
};

/**
 * Compares master and details entities in 2 arrays of entities, where the first item is always
 * the master entity and the rest the child entities.
 * Normally this would be an array compare, but the order of entities are not guaranteed in
 * the store or response, so there must be an individual one-way lookup comparing
 * the incoming with the existing.
 *
 * @param first
 * @param second
 */
const caseEquals = (a: StoreData, b: StoreData) =>
  a.every(localChild => {
    // With local changes a as source, find matches and deep equal check each object
    //
    // NOTE: to prevent an double save loop due to incoming calculations triggering an
    // autosave, we would need metadata on those fields up-front.
    // That is: field1:'yo' dispatched, the server calculates field2:'yo that' then this code
    // would not accept that without triggering yet another background save upon return
    const savedChild = b.find(
      el => JSON.stringify(el.id) === JSON.stringify(localChild.id)
    );
    return deepEquals(localChild, savedChild);
  });

/**
 * Reducers for case. Pattern to be able to extract modules when it grows.
 */
const reducers: ActionMap<UiState> = {
  /**
   * Run when a component wants codetable data
   */
   [ACTION.LOAD_OPTIONS_CODES]: (
    state: UiState, 
    action: RequestCodetable
  ): CaseState => {
    const contextJSON = toJSON(action.context);
    return {
      ...state,
      tempdata: {
        ...state.tempdata,
        options: {
          ...state.tempdata.options,
          [contextJSON]: {
            ...state.tempdata.options?.[contextJSON],
            fetching: true,
            args: action.loadOptions
          }
        }  
      }
    };
  },

  /**
   * Runs if a codetable data request failed
   */
  [ACTION.LOAD_OPTIONS_CODES_ERROR]: (
    state: UiState,
    action: RequestCodetableError
  ): UiState => {
    const contextJSON = toJSON(action.context);
    return {
      ...state,
      tempdata: {
        ...state.tempdata,        
        options: { 
          ...state.tempdata.options,
          [contextJSON]: {
            ...state.tempdata.options?.[contextJSON],
            ...(deepEquals(action.loadOptions, state.tempdata.options?.[contextJSON]?.args) ?
              {
                data: null,
                args: action.loadOptions,
                fetching: false,
                error: true
              } : {}
            )
          }
        }
      }
    };
  },

  /**
   * Run when a component receives its code data
   */
  [ACTION.LOAD_OPTIONS_CODES_OK]: (
    state: UiState,
    action: ReceiveCodetable
  ): CaseState => {
    const codes = action.data?.rootCodes?.map(codeItemToStoreValue);
    const contextJSON = toJSON(action.context);  
    return {
      ...state,
      tempdata: {
        ...state.tempdata,
        options: {
          ...state.tempdata.options,
          [contextJSON]: {
            ...state.tempdata.options?.[contextJSON],
            ...(deepEquals(action.loadOptions, state.tempdata.options?.[contextJSON]?.args) ?
              {
                data: codes,
                args: action.loadOptions,
                fetching: false,
                error: null
              } : {}
            )
          }
        }
      }
    };
  },

  /**
   * Run when a component wants codetable data
   */
  [ACTION.LOAD_CATEGORY_CODES]: (
    state: UiState, 
    action: RequestCodetableCategory
  ): CaseState => {
    const contextJSON = toJSON(action.context);
    return {
      ...state,
      tempdata: {
        ...state.tempdata,
        categoryOptions: {
          ...state.tempdata.categoryOptions,
          [contextJSON]: {
            ...state.tempdata.categoryOptions?.[contextJSON],
            fetching: true,
            args: action.categoryOptions
          }
        }  
      }
    };
  },
  /**
   * Runs if a codetable data request failed
   */
  [ACTION.LOAD_CATEGORY_CODES_ERROR]: (
    state: UiState,
    action: RequestCodetableCategoryError
  ): CaseState => {
    const contextJSON = toJSON(action.context);
    return {
      ...state,
      tempdata: {
        ...state.tempdata,        
        categoryOptions: {
          ...state.tempdata.categoryOptions, 
          [contextJSON]: {
            ...state.tempdata.categoryOptions?.[contextJSON],
            ...(deepEquals(action.categoryOptions, state.tempdata.categoryOptions?.[contextJSON]?.args) ?
              {
                data: null,
                args: action.categoryOptions,
                fetching: false,
                error: true
              } : {}
            )
          }
        }
      }
    };
  },

  /**
   * Run when a component receives its code data
   */
  [ACTION.LOAD_CATEGORY_CODES_OK]: (
    state: UiState,
    action: ReceiveCodetableCategory
  ): CaseState => {
    const codes = action.data?.rootCodes?.map(codeItemToStoreValue);
    const contextJSON = toJSON(action.context);
    return {
      ...state,
      tempdata: {
        ...state.tempdata,
        categoryOptions: {
          ...state.tempdata.categoryOptions,
          [contextJSON]: {
            ...state.tempdata.categoryOptions?.[contextJSON],
            ...(deepEquals(action.categoryOptions, state.tempdata.categoryOptions?.[contextJSON]?.args) ?
              {
                data: codes,
                args: action.categoryOptions,
                fetching: false,
                error: null
              } : {}
            )
          }
        }
      }
    };
  },

  /**
   * Run when a component fires a onChange event
   */
  [ACTION.VALUE_CHANGE]: (state: UiState, action: ValueChange): UiState => {
    const currentEditing = state.editing || INITIAL_EDIT_STATE;

    const targetEntity = getById(currentEditing.values, action.storePath);
    const currentIndex = currentEditing.values.indexOf(targetEntity);

    const currentValues = [
      ...currentEditing.values.slice(0, currentIndex),
      {
        ...targetEntity,
        [action.dataTag]: action.value
      },
      ...currentEditing.values.slice(currentIndex + 1)
    ];

    const modified = !caseEquals(currentValues, currentEditing.persistedValues);

    return {
      ...state,
      editing: {
        ...currentEditing,
        values: currentValues,
        hasChanges: modified
      }
    };
  },

  [ACTION.REQUEST_IMMEDIATE_BACKGROUND_SAVE]: (
    state: UiState,
    action: RequestImmediateBackgroundSave
  ): UiState => {
    return {
      ...state,
      editing: {
        ...state.editing,

        // If a background save is already in process, we queue up another one
        // to fire immediately after the current (2). If not, we set the counter to 1.
        immediateSaveRequests: state.editing.fetching ? 2 : 1
      }
    };
  },

  /**
   * Run when a component wants to save the data
   */
  [ACTION.BACKGROUND_SAVE]: (
    state: UiState,
    action: BeginBackgroundSave
  ): UiState => {
    return {
      ...state,
      editing: {
        ...state.editing,
        savedValues: state.editing.values,
        fetching: true
      }
    };
  },

  /**
   * Run when a component finished saving the data
   */
  [ACTION.BACKGROUND_SAVE_OK]: (
    state: UiState,
    action: BackgroundSaveComplete
  ): UiState => {
    if (!state.editing) {
      return state;
    }

    // incoming is the saved case entity returned from the last save request
    const incomingEntity = action.data;

    // If a delete was initiated, it must be removed client side after the server save succeeds.
    // However, there are edge cases. If an item was both added and deleted in the timeframe of a
    // background save, we're in a trickier situation. This would call for a correlation id.

    // Deletes that are pending locally should be left in state.editing.values.
    // Deletes that have been sent to the server (eg. existed in state.editing.savedValues) should be removed
    const pendingDeletesInSavedValues = (item: any) =>
      !item.operation ||
      !state.editing.savedValues.find(
        e => !!e.operation && deepEquals(e.id, item.id)
      );

    // All deletes that were pending in state.editing.savedValues should be removed.
    const pendingDeletes = (item: any) => !item.operation;
    const savedValues = state.editing.savedValues || [];
    // if an entity is not part of the server data and it was part of the latest save, we interpret it as a server-side removal.
    const entitiesRemovedOnServer = savedValues.filter(
      item =>
        pendingDeletes(item) &&
        !incomingEntity.find(e => deepEquals(e.id, item.id))
    );

    // To really know if *user* editing has occured while saving in the background, we'll merge
    // ( 1. local values + 2. returned server values + 3. values sent to server on last save)
    // and then compare again with current values.
    //
    // NOTE: this merge does not support server calculations, they are detected as local changes for time being
    const userData = state.editing.values.filter(pendingDeletesInSavedValues);
    const lastServerSavedValues = savedValues.filter(
      pendingDeletes
    );

    const currentValues = getPatchedState(
      userData,
      incomingEntity,
      lastServerSavedValues
    )
      // except entities that were removed server-side
      .filter(
        item => !entitiesRemovedOnServer.find(e => deepEquals(e.id, item.id))
      );

    const modified = !caseEquals(currentValues, incomingEntity);

    return {
      ...state,
      editing: {
        ...state.editing,
        savedValues: undefined,
        values: currentValues,
        etag: action.etag,
        persistedValues: incomingEntity,
        hasChanges: modified,
        metadata: action.metadata,
        fetching: false,
        pristine: false, // indicate that user has changed the draft at least once
        immediateSaveRequests: state.editing.immediateSaveRequests
          ? state.editing.immediateSaveRequests - 1
          : 0
      }
    };
  },

  /**
   * Run when a component was not able to save the data
   */
  [ACTION.BACKGROUND_SAVE_ERROR]: (
    state: UiState,
    action: BackgroundSaveFailed
  ): UiState => {
    return {
      ...state,
      editing: {
        ...state.editing,
        savedValues: undefined,
        values: state.editing ? state.editing.persistedValues : undefined,
        fetching: false,
        hasChanges: false,
        immediateSaveRequests:
          state.editing && state.editing.immediateSaveRequests
            ? (state.editing.immediateSaveRequests - 1)
            : 0
      }
    };
  },

  /**
   * Run when user triggers submit case for a draft
   */
  [ACTION.CASE_SUBMIT]: (state: UiState, action: BeginCaseSave): UiState => {
    return {
      ...state,
      editing: {
        ...state.editing,
        loadValidationMetadata: true,
        submitting: true,
        continueEditing: action.continueEditing
      }
    };
  },

  /**
   * Run when case save api is called
   */
  [ACTION.REQUEST_CASE_SUBMIT]: (
    state: UiState,
    action: RequestCaseSave
  ): UiState => {
    return {
      ...state,
      editing: {
        ...state.editing,
        submitting: true,
        fetching: true
      }
    };
  },

  /**
   * Run when case saving is finished
   */
  [ACTION.CASE_SUBMIT_OK]: (
    state: UiState,
    action: CaseSaveComplete
  ): UiState => {
    return {
      ...state,
      editing: undefined
    };
  },

  /**
   * Run when case saving failed
   */
  [ACTION.CASE_SUBMIT_ERROR]: (
    state: UiState,
    action: CaseSaveFailed
  ): UiState => {
    return {
      ...state,
      editing: {
        ...state.editing,
        loadValidationMetadata: true,
        submitting: false,
        fetching: false
      }
    };
  },

  [ACTION.INSPECT_COMPONENT]: (state: UiState, action: any): UiState => {
    return {
      ...state,
      ...{
        $inspectedComponent: action.payload,
        $inspectedError: action.error
      }
    } as any;
  },

  [ACTION.INSPECT_PREVIEW_EDIT]: (state: UiState, action: any): UiState => {
    return {
      ...state,
      ...{
        $inspectedChanges: action.payload
      }
    } as any;
  }
};

/**
 * Export a function that maps the given action to our ActionMap
 */
export const createUiReducers = (
  reducerPredicate: (action: any) => boolean
): Reducer<UiState, Action> => {
  return (state: UiState = {} as any, action: any) => {
    const r = reducerPredicate(action) && reducers[action.type];
    return (r && r(state, action)) || state;
  };
};
